import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/lib/useIsMobile";

/**
 * Returns true when the current user is allowed to mutate admin/staff workspace
 * data (create, edit, delete on Quotes / Clients / Products / Vessels / Tenders).
 *
 * - Admins always have edit rights (desktop AND mobile).
 * - Staff users have edit rights on desktop only when their account's
 *   `can_edit` flag is set. On mobile viewports (< md breakpoint) staff
 *   are ALWAYS view-only — per product decision, the mobile shell is a
 *   read-only browsing experience so field staff can't accidentally edit
 *   quotes / vessels on their phone.
 * - Clients are gated separately by `isClient` checks in each page and never
 *   reach the write paths these helpers protect — we treat them as "no edit"
 *   here so the same `canEdit && (...)` wrapper hides admin buttons from them
 *   if they ever land on an admin route.
 *
 * The backend enforces the same rule via `require_editor`; this hook only
 * hides the UI so View-Only staff (and mobile staff) don't see buttons that
 * would just 403.
 */
export function useCanEdit() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  if (!user) return false;
  if (user.role === "admin") return true;
  if (user.role === "staff") {
    if (isMobile) return false;
    return user.can_edit === true;
  }
  return false;
}
